import { DealDetail, EvergreenNoticePeriod, NegotiationFieldMeta, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import { createInvalidValueValidationIssue, getDateEditorFormat, getNegotiatedValue, Severity } from 'model'
import { translateEvergreenNoticePeriodToNumber } from 'model/dealUtils'
import moment from 'moment'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isStockBorrowingLendingEnabledSelector } from 'selectors/featureSelectors'
import { platformTradeDateSelector } from 'selectors/platformSelectors'
import { DateLike } from 'utils'
import { TableColumn, TableOptions } from 'view/components/panels/Table'
import { useAuthorisedUser, useIsAdmin, useUserPreference } from 'view/hooks'
import { DealFieldNames } from 'view/routes/deals/components/DealsGridColumnNames'
import { getDealModificationColour } from 'view/routes/deals/components/utils'
import { getStartDateModificationType, getStringModificationType } from 'view/routes/requests/shared/deltaGetters'
import {
  getFeeFormatOptions,
  getHaircutFormatOptions,
  getNotionalFormatOptions,
  getRateFormatOptions,
  getStartConsiderationFormatOptions
} from 'view/shared/datagrid'
import { OrganisationDecisionMakerSelectboxField, TradingDatePickerField } from 'view/shared/entity'
import { useGetEffectiveDateInfo } from '../useGetEffectiveDateInfo'
import { isPropertyEmptyInAll } from '../utils'

export const useTerminationColumns = (
  onUpdate: (payload: { dealId: string; value: any; field: NegotiationFieldMeta; rawValue?: any }) => void
) => {
  const userPreference = useUserPreference()
  const isAdmin = useIsAdmin()
  const user = useAuthorisedUser()
  const platformTradeDate = useSelector(platformTradeDateSelector)
  const getEffectiveDateInfo = useGetEffectiveDateInfo(true)
  const stockBorrowingLendingEnabled = useSelector(isStockBorrowingLendingEnabledSelector)

  const columns: TableColumn<DealDetail>[] = useMemo(() => {
    const getDealValidationIssues = (
      data: DealDetail
    ): { minEffectiveDate: Date | null; maxEffectiveDate: Date | null; dealValidationIssues: ValidationIssue[] } => {
      const dealEffectiveDate = getNegotiatedValue(
        data.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.END_DATE]?.negotiableFieldRequired
      )
      const evergreenNoticePeriod = data.deal.evergreenNoticePeriod

      const evergreenNoticePeriodNumber = evergreenNoticePeriod ? translateEvergreenNoticePeriodToNumber(evergreenNoticePeriod) : null

      const [, minEffectiveDate, maxEffectiveDate, dealIsBeforeCutOffTime] = getEffectiveDateInfo(data)

      const dealValidationIssues = (data.servicingSideDetails?.validationIssues ?? []).concat(
        uiValidationIssues(platformTradeDate, dealEffectiveDate, evergreenNoticePeriod, evergreenNoticePeriodNumber, dealIsBeforeCutOffTime)
      )

      return { minEffectiveDate, maxEffectiveDate, dealValidationIssues }
    }

    return [
      {
        colId: 'dealId',
        headerName: Locale.translate(Translation.TC_DEAL_ID),
        valueGetter: ({ data }: { data: DealDetail }) => {
          return data.deal.dealId
        },
        styles: { left: true }
      },
      {
        colId: 'collateral',
        headerName: Locale.translate(Translation.TC_COLLATERAL),
        valueGetter: ({ data }) => data.collateral?.name ?? data.collateralBasket?.name ?? ''
      },
      {
        colId: 'rightOfExit',
        headerName: Locale.translate(Translation.TC_ROE),
        valueGetter: ({ data }) => Locale.formatBoolean(data.deal?.rightOfExit ? true : false)
      },
      {
        colId: 'notional',
        headerName: Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat }),
        valueGetter: ({ data }) => Locale.formatNumber(data.deal.notional, getNotionalFormatOptions(userPreference.amountFormat, false))
      },
      {
        colId: 'startConsideration',
        headerName: Locale.translate(Translation.TC_ABBR_START_CONSIDERATION),
        valueGetter: ({ data }) => Locale.formatNumber(data.deal.startConsideration, getStartConsiderationFormatOptions())
      },
      {
        colId: 'rate',
        headerName: Locale.translate(Translation.TC_RATE),
        valueGetter: ({ data }) => Locale.formatNumber(data.deal.rate, getRateFormatOptions(data.referenceRate)),
        hide: ({ rows }) => isPropertyEmptyInAll(rows, dealDetail => dealDetail.deal.rate)
      },
      {
        colId: 'fee',
        headerName: Locale.translate(Translation.TC_FEE),
        valueGetter: ({ data }) => Locale.formatNumber(data.deal.fee, getFeeFormatOptions()),
        hide: ({ rows }) => {
          return stockBorrowingLendingEnabled === false || isPropertyEmptyInAll(rows, dealDetail => dealDetail.deal.fee)
        }
      },
      {
        colId: 'haircut',
        headerName: Locale.translate(Translation.TC_HAIRCUT),
        valueGetter: ({ data }) => {
          return Locale.formatNumber(
            data.deal.haircut,
            getHaircutFormatOptions(undefined, userPreference.haircutSign, true, data.deal.direction)
          )
        },
        hide: ({ rows }) => isPropertyEmptyInAll(rows, dealDetail => dealDetail.deal.haircut)
      },
      {
        colId: 'effectiveDate',
        headerName: Locale.translate(Translation.TC_EFFECTIVE_DATE),
        cellRenderer: ({ data }) => {
          const dealEffectiveDate = getNegotiatedValue(
            data.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.END_DATE]?.negotiableFieldRequired
          )

          const { minEffectiveDate, maxEffectiveDate, dealValidationIssues } = getDealValidationIssues(data)

          return (
            <TradingDatePickerField
              label=""
              fieldName={NegotiationFieldMeta.END_DATE}
              currency={data.deal.currency}
              minDate={minEffectiveDate ?? undefined}
              maxDate={maxEffectiveDate ?? undefined}
              dateFormat={getDateEditorFormat(userPreference.dateFormat)}
              validationField={DealFieldNames.DEAL_END_DATE}
              validationIssues={dealValidationIssues}
              underline={getDealModificationColour(
                () => getStartDateModificationType(data.servicingSideDetails?.negotiationFields?.END_DATE?.negotiableFieldRequired),
                data
              )}
              value={dealEffectiveDate}
              onUpdate={(value: string | null) => onUpdate({ dealId: data.deal.dealId, value, field: NegotiationFieldMeta.END_DATE })}
              dataTestId={'ServicingTerminationDialog-DealEffectiveDateTradingDatePickerField'}
            />
          )
        },
        styles: { overflow: 'visible' }
      },
      {
        colId: 'investmentDecisionMaker',
        headerName: Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER_ABBR),
        cellRenderer: ({ data }) => {
          const idmField = data.ownOrder
            ? NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER
            : NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER

          const { dealValidationIssues } = getDealValidationIssues(data)
          const organisationId = isAdmin ? data.deal.initiatorOrganisationId : user.organisationId

          return (
            <OrganisationDecisionMakerSelectboxField
              label=""
              fieldName={idmField}
              value={getNegotiatedValue(data.servicingSideDetails?.sideFields?.[idmField]?.negotiableFieldRequired)}
              onUpdate={(value, field) => onUpdate({ value, field, dealId: data.deal.dealId })}
              validationField={
                data.ownOrder
                  ? DealFieldNames.DEAL_INITIATOR_INVESTMENT_DECISION_MAKER
                  : DealFieldNames.DEAL_RESPONDER_INVESTMENT_DECISION_MAKER
              }
              validationIssues={dealValidationIssues}
              underline={getDealModificationColour(
                () => getStringModificationType(data.servicingSideDetails?.sideFields?.[idmField]?.negotiableFieldRequired),
                data
              )}
              dataTestId={'ServicingSubstitutionDialog-DecisionMakerSelectboxField-Investment'}
              organisationId={organisationId}
            />
          )
        }
      },
      {
        colId: 'executionDecisionMaker',
        headerName: Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER_ABBR),
        cellRenderer: ({ data }) => {
          const edmField = data.ownOrder
            ? NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER
            : NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER

          const { dealValidationIssues } = getDealValidationIssues(data)
          const organisationId = isAdmin ? data.deal.initiatorOrganisationId : user.organisationId

          return (
            <OrganisationDecisionMakerSelectboxField
              label=""
              fieldName={edmField}
              value={getNegotiatedValue(data.servicingSideDetails?.sideFields?.[edmField]?.negotiableFieldRequired)}
              onUpdate={(value, field) => onUpdate({ value, field, dealId: data.deal.dealId })}
              validationField={
                data.ownOrder
                  ? DealFieldNames.DEAL_INITIATOR_EXECUTION_DECISION_MAKER
                  : DealFieldNames.DEAL_RESPONDER_EXECUTION_DECISION_MAKER
              }
              validationIssues={dealValidationIssues}
              underline={getDealModificationColour(
                () => getStringModificationType(data.servicingSideDetails?.sideFields?.[edmField]?.negotiableFieldRequired),
                data
              )}
              dataTestId={'ServicingSubstitutionDialog-DecisionMakerSelectboxField-Execution'}
              organisationId={organisationId}
            />
          )
        }
      }
    ]
  }, [
    getEffectiveDateInfo,
    isAdmin,
    onUpdate,
    platformTradeDate,
    stockBorrowingLendingEnabled,
    user.organisationId,
    userPreference.amountFormat,
    userPreference.dateFormat,
    userPreference.haircutSign
  ])

  return columns
}

function uiValidationIssues(
  platformTradeDate: DateLike | null,
  dealEffectiveDate: any,
  evergreenNoticePeriod: EvergreenNoticePeriod | null,
  evergreenNoticePeriodNumber: number | null,
  dealIsBeforeCutOffTime: boolean
) {
  const validationIssues: ValidationIssue[] = []
  if (platformTradeDate && dealEffectiveDate && moment(dealEffectiveDate).isSame(platformTradeDate, 'day') && !dealIsBeforeCutOffTime) {
    validationIssues.push(
      createInvalidValueValidationIssue('endDate', Locale.translate(Translation.SC_EFFECTIVE_DATE_TODAY_WARNING_MESSAGE), Severity.WARN)
    )
  }

  if (evergreenNoticePeriod) {
    validationIssues.push(
      createInvalidValueValidationIssue(
        'endDate',
        Locale.translate(Translation.TC_EVERGREEN_NOTICE_PERIOD_INFO_MESSAGE, {
          evergreenNoticePeriod: evergreenNoticePeriodNumber
        }),
        Severity.INFO
      )
    )
  }

  return validationIssues
}

export const options: TableOptions<DealDetail> = {
  getRowId: row => row.deal.dealId,
  autoExpandRows: true
}
